import React from "react"
import { useTranslation } from "react-i18next"

import { CurrencyField } from "@/components/fields/currency-field"
import { FlowDirectionTooltipContent } from "@/components/fields/flow-direction-tooltip-content"
import { FormDateRangeField } from "@/components/fields/form-date-range-field/form-date-range-field"
import { UsageUnitField } from "@/components/fields/usage-unit-field"
import { useActiveOrganizationId } from "@/contexts"
import type {
  BillPdfPreviewRow,
  BillPdfPreviewRowEdit,
} from "@/modules/data/services/use-bill-pdf-upload-session"
import { DialogTitle } from "@/nzds/feedback/dialog-title/dialog-title"
import { FormSelectField } from "@/nzds/forms/form-select-field/form-select-field"
import { FormTextField } from "@/nzds/forms/form-text-field/form-text-field"
import { DialogForm } from "@/nzds/forms/form/dialog-form"
import { ReadonlyField } from "@/nzds/forms/readonly-field/readonly-field"
import { useForm } from "@/nzds/forms/use-form/use-form"
import type { Option } from "@/nzds/inputs/inputs.types"
import {
  isoDateSchema,
  optionalNumberStringSchema,
  refineDateRange,
  standardStringSchema,
} from "@/schemas"
import {
  buildResourceFilterOptions,
  getDistinctMeterResourceCodes,
} from "@/services/bill/bill-resource"
import { useResourceProviders } from "@/services/credential/use-resource-providers"
import { useAllMeters } from "@/services/meter"
import { useSitePreviews } from "@/services/useSitePreviews/useSitePreviews"
import { useFormatFlowDirection } from "@/utils/bill/format-flow-direction"
import { EMPTY_DATA_PLACEHOLDER } from "@/utils/constants"
import { z } from "zod"

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
} from "@mui/material"

interface BillPdfPreviewEditDialogProps {
  onClose: () => void
  onSave: (rowId: string, edit: BillPdfPreviewRowEdit) => Promise<void>
  open: boolean
  row: BillPdfPreviewRow | null
}

interface BillPdfPreviewEditFormProps {
  onClose: () => void
  onSave: (rowId: string, edit: BillPdfPreviewRowEdit) => Promise<void>
  row: BillPdfPreviewRow
}

const editSchema = z
  .object({
    cost: optionalNumberStringSchema,
    currency: standardStringSchema.default(""),
    endDate: isoDateSchema,
    meterId: standardStringSchema.default(""),
    resourceType: standardStringSchema.default(""),
    siteId: standardStringSchema.default(""),
    startDate: isoDateSchema,
    usage: optionalNumberStringSchema,
    usageUnit: standardStringSchema.default(""),
    utilityProvider: standardStringSchema.default(""),
  })
  .superRefine(refineDateRange)

const EMPTY_OPTION: Option = { label: EMPTY_DATA_PLACEHOLDER, value: "" }

const toText = (value: null | number | string | undefined): string =>
  value == null ? "" : String(value)

const toEditFormValues = (row: BillPdfPreviewRow): BillPdfPreviewRowEdit => ({
  cost: toText(row.cost),
  currency: toText(row.currency).toUpperCase(),
  endDate: toText(row.endDate),
  meterId: toText(row.meterId),
  resourceType: toText(row.resourceType),
  siteId: toText(row.siteId),
  startDate: toText(row.startDate),
  usage: toText(row.usage),
  usageUnit: toText(row.usageUnit),
  utilityProvider: toText(row.utilityProvider),
})

// The row names its site and meter before their lists have loaded
const withSelectedOption = (
  options: Option[],
  value: string,
  label: string
): Option[] =>
  value === "" || options.some((option) => option.value === value)
    ? options
    : [...options, { label, value }]

const BillPdfPreviewEditForm: React.FC<BillPdfPreviewEditFormProps> = ({
  onClose,
  onSave,
  row,
}) => {
  const { t } = useTranslation("data")
  const { t: tCommon } = useTranslation("common")
  const { activeOrganizationId } = useActiveOrganizationId()
  const organizationId = activeOrganizationId ?? ""
  const { formatFlowDirection } = useFormatFlowDirection()
  const form = useForm({
    defaultValues: toEditFormValues(row),
    schema: editSchema,
  })
  const siteId = form.watch("siteId")
  const meterId = form.watch("meterId")

  const { sitePreviewsData } = useSitePreviews()
  const { metersData: siteMeters, metersIsComplete: siteMetersAreComplete } =
    useAllMeters({
      apiQueryOptions: {
        where: { organization_id: organizationId, site_id: siteId },
      },
      enabled: siteId !== "",
    })
  const { metersData: organizationMeters, metersIsComplete } = useAllMeters({
    apiQueryOptions: {
      includes: ["meter_source_type"],
      where: { organization_id: organizationId },
    },
  })
  const { resourceProvidersData } = useResourceProviders({ organizationId })

  const siteOptions = withSelectedOption(
    [
      EMPTY_OPTION,
      ...(sitePreviewsData ?? []).map((site) => ({
        label: site.name,
        value: site.id,
      })),
    ],
    siteId,
    toText(row.siteName)
  )
  const meterOptions = withSelectedOption(
    [
      EMPTY_OPTION,
      ...(siteMeters ?? []).map((meter) => ({
        label: meter.meterCode,
        value: meter.id ?? "",
      })),
    ],
    meterId,
    toText(row.meterCode)
  )
  const providerOptions = [
    EMPTY_OPTION,
    ...(resourceProvidersData ?? []).map((provider) => ({
      label: provider.name,
      value: provider.name,
    })),
  ]
  const resourceCodes = getDistinctMeterResourceCodes(
    metersIsComplete ? (organizationMeters ?? []) : []
  )
  const storedResource = toText(row.resourceType)
  const resourceOptions = [
    EMPTY_OPTION,
    ...buildResourceFilterOptions(
      storedResource === "" || resourceCodes.includes(storedResource)
        ? resourceCodes
        : [...resourceCodes, storedResource],
      (key) => tCommon(key)
    ),
  ]
  // Only an unmatched row has a parsed code to show; a matched meter is not a hint
  const meterHint = row.meterId == null ? toText(row.meterCode) : ""

  React.useEffect(() => {
    // A meter belongs to one site, so a different site never keeps it
    const subscription = form.watch((_values, { name }) => {
      if (name === "siteId") {
        form.setValue("meterId", "", { shouldDirty: true })
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [form])

  const handleSave = async (values: BillPdfPreviewRowEdit): Promise<void> => {
    try {
      await onSave(row.id, values)
    } catch {
      // The session service notifies; the dialog stays open for another try
      return
    }

    onClose()
  }

  return (
    <DialogForm
      aria-labelledby="bill-pdf-preview-edit-title"
      form={form}
      onSubmit={handleSave}
    >
      <DialogContent>
        <Stack sx={{ gap: 2, pt: 1 }}>
          <FormSelectField
            label={tCommon("fields.siteName.title")}
            name="siteId"
            options={siteOptions}
            variant="stacked"
          />
          <FormSelectField
            disabled={siteId === ""}
            helperText={
              meterId === "" && meterHint !== ""
                ? t("upload.billPdf.edit.meterHint", { meterCode: meterHint })
                : undefined
            }
            label={tCommon("fields.meterCode.title")}
            name="meterId"
            options={meterOptions}
            // Keep the stored meter registered while its list is loading or failed
            readOnly={siteId !== "" && !siteMetersAreComplete}
            variant="stacked"
          />
          <FormSelectField
            label={tCommon("fields.utilityProvider.title")}
            name="utilityProvider"
            options={providerOptions}
            variant="stacked"
          />
          <FormSelectField
            label={tCommon("fields.resource.title")}
            name="resourceType"
            options={resourceOptions}
            variant="stacked"
          />
          <FormDateRangeField required={false} />
          <ReadonlyField
            label={tCommon("fields.flowDirection.title")}
            tooltipContent={<FlowDirectionTooltipContent />}
            value={formatFlowDirection(row.flowDirection)}
          />
          <FormTextField
            label={tCommon("fields.usage.title")}
            name="usage"
            variant="stacked"
          />
          <UsageUnitField currentValue={row.usageUnit} />
          <FormTextField
            label={tCommon("fields.cost.title")}
            name="cost"
            variant="stacked"
          />
          <CurrencyField currentValue={toText(row.currency).toUpperCase()} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={form.formState.isSubmitting}
          onClick={onClose}
          variant="outlined"
        >
          {tCommon("button.cancel")}
        </Button>
        <Button
          loading={form.formState.isSubmitting}
          type="submit"
          variant="contained"
        >
          {tCommon("button.save")}
        </Button>
      </DialogActions>
    </DialogForm>
  )
}

export const BillPdfPreviewEditDialog: React.FC<
  BillPdfPreviewEditDialogProps
> = ({ onClose, onSave, open, row }) => {
  const { t } = useTranslation("data")
  const [isSaving, setIsSaving] = React.useState(false)
  const [displayedRow, setDisplayedRow] = React.useState(row)
  if (row != null && row !== displayedRow) {
    setDisplayedRow(row)
  }

  const handleExited = (): void => {
    if (row == null) {
      setDisplayedRow(null)
    }
  }

  // Dismissing mid-save would let this save's close land on a later row's dialog
  const handleSave = async (
    rowId: string,
    edit: BillPdfPreviewRowEdit
  ): Promise<void> => {
    setIsSaving(true)
    try {
      await onSave(rowId, edit)
    } finally {
      setIsSaving(false)
    }
  }
  const handleClose = isSaving ? undefined : onClose

  return (
    <Dialog
      aria-labelledby="bill-pdf-preview-edit-title"
      data-testid="bill-pdf-preview-edit-dialog"
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      slotProps={{ transition: { onExited: handleExited } }}
    >
      <DialogTitle id="bill-pdf-preview-edit-title" onClose={handleClose}>
        {t("upload.billPdf.edit.title")}
      </DialogTitle>
      {displayedRow ? (
        <BillPdfPreviewEditForm
          key={displayedRow.id}
          onClose={onClose}
          onSave={handleSave}
          row={displayedRow}
        />
      ) : null}
    </Dialog>
  )
}
